import type {Mapper} from '@automapper/core';
import type {DownloadColumn, IDownloadByLinkService} from '@igp/shared/api';
import {ServerResponseStatus} from '@igp/shared/api';
import {map} from '@igp/shared/otel';
import {
    type DownloadStrategyRequest,
    type DownloadStrategyResponse,
    EntityDownloadClientStrategy,
    type IDownloadStrategy,
} from '@igp/shared/redux/download';
import {EntityType} from '@models';
import {inject, injectable} from 'inversify';
import type {Observable} from 'rxjs';

import {ServiceTypes} from '@ioc-container';

import {PayoutReportServiceTypes} from './constants';
import {payoutReportMappingKeys} from './mapper';
import type {PayoutReportService} from './service';
import type {PayoutReportDownloadFields} from './types';
import {type PayoutReportViewModelKeys} from './types';

@injectable()
export class PayoutReportDownloadClientStrategy extends EntityDownloadClientStrategy<PayoutReportViewModelKeys, EntityType> {
    constructor(@inject(ServiceTypes.AutoMapper) mapper: Mapper, @inject(PayoutReportServiceTypes.service) service: PayoutReportService) {
        super(mapper, service, EntityType.PayoutReport, {
            viewModelKeys: payoutReportMappingKeys.viewModelsKeys,
            queryFields: payoutReportMappingKeys.queryItemsKeys,
            model: payoutReportMappingKeys.model,
            downloadModel: payoutReportMappingKeys.downloadModel,
        });
    }
}

@injectable()
export class PayoutReportDownloadServerStrategy implements IDownloadStrategy<PayoutReportViewModelKeys> {
    private readonly _mapper: Mapper;
    private readonly _service: IDownloadByLinkService<PayoutReportDownloadFields>;

    constructor(
        @inject(ServiceTypes.AutoMapper) mapper: Mapper,
        @inject(PayoutReportServiceTypes.service)
        service: IDownloadByLinkService<PayoutReportDownloadFields>
    ) {
        this._mapper = mapper;
        this._service = service;
    }

    process(request: DownloadStrategyRequest<PayoutReportViewModelKeys>): Observable<DownloadStrategyResponse<PayoutReportViewModelKeys>> {
        const columns: DownloadColumn<PayoutReportDownloadFields>[] = request.keys?.map<DownloadColumn<PayoutReportDownloadFields>>(k => {
            const queryField = this._mapper.map<PayoutReportViewModelKeys, PayoutReportDownloadFields>(
                k,
                payoutReportMappingKeys.viewModelsKeys,
                payoutReportMappingKeys.downloadFields
            );
            return {
                name: request.headers[k] ?? k,
                field: queryField,
            };
        });
        return this._service
            .getDownloadLink({
                filter: request.filter,
                columns,
                report_name: request.filename,
            })
            .pipe(
                map(response => ({
                    status: response?.status === ServerResponseStatus.Success ? 'Success' : 'Failed',
                    link: response?.responsePayload,
                    mode: 'server',
                }))
            );
    }
}
